import { Component, Vue, Watch } from 'vue-property-decorator';
import UserService from '@/services/user.service';
import InputPassword from '@/components/inputs/InputPassword/InputPassword.vue';
import LocalStoreService from '@/utils/local.store';
import { PasswordValidationResult, PasswordValidationFailure } from '@/models/PasswordValidationResult';


@Component({
  components: {
    InputPassword,
  },
})
export default class PasswordModification extends Vue {
  // Properties
  public errorMessage: string = '';
  public successMessage: string = '';
  private userName: string =  '';
  private oldPassword: string = '';
  private newPassword: string = '';
  private repeatNewPassword: string = '';

  // Password validation properties
  private passwordValidationResult: PasswordValidationResult | null = null;
  private isValidatingPassword: boolean = false;

  // Lifecycle hook
  public mounted() {
    this.userName = LocalStoreService.getItem(LocalStoreService.localItemKeys.proUsername) || '';
  }

  // Methods

  /**
   * Watch password changes and validate
   */
  @Watch('newPassword')
  protected async onPasswordChange(newPassword: string): Promise<void> {
    if (newPassword && newPassword.length > 0) {
      this.isValidatingPassword = true;
      this.passwordValidationResult = await UserService.validatePassword(newPassword);
      this.isValidatingPassword = false;
    } else {
      this.passwordValidationResult = null;
    }
  }

  /**
   * Get password strength color based on score
   */
  public get passwordStrengthColor(): string {
    if (!this.passwordValidationResult) return 'grey';
    const score = this.passwordValidationResult.score;
    if (score < 30) return 'red';
    if (score < 60) return 'orange';
    if (score < 80) return 'amber';
    return 'green';
  }

  /**
   * Get password strength label
   */
  public get passwordStrengthLabel(): string {
    if (!this.passwordValidationResult) return '';
    const score = this.passwordValidationResult.score;
    if (score < 30) return this.$t('password.validation.weak').toString();
    if (score < 60) return this.$t('password.validation.medium').toString();
    if (score < 80) return this.$t('password.validation.good').toString();
    return this.$t('password.validation.strong').toString();
  }

  /**
   * Check if passwords match
   */
  public get passwordsMatch(): boolean {
    return this.newPassword === this.repeatNewPassword && this.newPassword.length > 0;
  }

  /**
   * Check if password is valid for form submission
   */
  public get isPasswordValid(): boolean {
    return this.passwordValidationResult !== null
      && this.passwordValidationResult.isValid
      && this.passwordsMatch
      && this.oldPassword.length > 0;
  }

  /**
   * Get validation failure messages
   */
  public getFailureMessages(): string[] {
    if (!this.passwordValidationResult || !this.passwordValidationResult.failures) {
      return [];
    }

    // Map backend enum string names to i18n keys
    const errorKeys: { [key: string]: string } = {
      'TooShort': 'tooShort',
      'TooLong': 'tooLong',
      'MissingDigit': 'missingDigit',
      'MissingUppercase': 'missingUppercase',
      'MissingLowercase': 'missingLowercase',
      'MissingSpecialCharacter': 'missingSpecialCharacter',
      'NotEnoughUniqueCharacters': 'notEnoughUniqueCharacters',
      'CommonPassword': 'commonPassword',
      'SequentialPattern': 'sequentialPattern',
      'RepetitivePattern': 'repetitivePattern'
    };

    return this.passwordValidationResult.failures
      .map((failure: PasswordValidationFailure) => {
        const key = errorKeys[failure];
        if (key) {
          return this.$t(`password.validation.errors.${key}`).toString();
        }
        return '';
      })
      .filter((msg: string) => msg !== '');
  }

  /**
   * Saves the newly entered password.
   */
  protected async saveNewPassword() {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.newPassword !== this.repeatNewPassword) {
      this.errorMessage = 'errorMgs.password.newPasswordsNotMatching';
      return;
    }

    if (!this.isPasswordValid) {
      this.errorMessage = 'password.validation.invalidPassword';
      return;
    }

    await UserService.saveNewPassword(this.userName, this.oldPassword, this.newPassword)
      .then(() => {
        this.successMessage = 'password.successfullyChanged';
      })
      .catch((error: any) => {
        if (error.response === undefined) {
          return;
        }

        switch (error.response.status) {
          case 401:
            this.errorMessage = 'errorMgs.password.oldPasswordNotMatching';
            break;
          case 404:
            this.errorMessage = 'errorMgs.password.usernameUnknown';
            break;
          default:
            this.errorMessage = 'errorMgs.password.savingPasswordError';
        }
      });
  }

}
